// src/dashboard/pages/teacher/Palmares/components/EditPalmaresDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaEraser, FaSave, FaSearch, FaTimes, FaTrash } from "react-icons/fa";

import {
  formatEventLabel,
  getAdherentFromPalmares,
  getAvatarSrc,
  getEventFromPalmares,
  getMemberDisplayName,
  toDateInputValue,
} from "../utils/palmaresDashboardUtils";

import VisibilitySwitch from "./VisibilitySwitch";

const initialForm = {
  event_palmares_id: "",
  date_palmares: "",
  titre_palmares: "",
  resultats_palmares: "",
  classement_palmares: "",
  observations_palmares: "",
  is_active: true,
};

export default function EditPalmaresDrawer({
  isOpen,
  onClose,
  palmares,
  onSubmit,
  onDelete,
  saving,
  deletingId,
  events = [],
}) {
  const [formData, setFormData] = useState(initialForm);
  const [eventQuery, setEventQuery] = useState("");

  useEffect(() => {
    if (!isOpen || !palmares) return;

    setFormData({
      event_palmares_id: palmares.event_palmares_id || "",
      date_palmares: toDateInputValue(palmares.date_palmares),
      titre_palmares: palmares.titre_palmares || "",
      resultats_palmares: palmares.resultats_palmares || "",
      classement_palmares: palmares.classement_palmares || "",
      observations_palmares: palmares.observations_palmares || "",
      is_active: !!palmares.is_active,
    });

    setEventQuery("");
  }, [isOpen, palmares]);

  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        handleClose();
      }
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, saving, formData]);

  const adherent = useMemo(() => {
    return getAdherentFromPalmares(palmares);
  }, [palmares]);

  const originalEvent = useMemo(() => {
    return getEventFromPalmares(palmares);
  }, [palmares]);

  const filteredEvents = useMemo(() => {
    const query = eventQuery.trim().toLowerCase();

    if (!query) return events;

    return events.filter((event) =>
      formatEventLabel(event).toLowerCase().includes(query)
    );
  }, [events, eventQuery]);

  const selectedEvent = useMemo(() => {
    const fromList = events.find(
      (event) => String(event.id) === String(formData.event_palmares_id)
    );

    return fromList || originalEvent;
  }, [events, formData.event_palmares_id, originalEvent]);

  const isDirty = useMemo(() => {
    if (!palmares) return false;

    return (
      String(formData.event_palmares_id || "") !==
        String(palmares.event_palmares_id || "") ||
      String(formData.date_palmares || "") !==
        String(toDateInputValue(palmares.date_palmares) || "") ||
      String(formData.titre_palmares || "") !==
        String(palmares.titre_palmares || "") ||
      String(formData.resultats_palmares || "") !==
        String(palmares.resultats_palmares || "") ||
      String(formData.classement_palmares || "") !==
        String(palmares.classement_palmares || "") ||
      String(formData.observations_palmares || "") !==
        String(palmares.observations_palmares || "") ||
      !!formData.is_active !== !!palmares.is_active
    );
  }, [formData, palmares]);

  const canSubmit = useMemo(() => {
    return (
      palmares?.id &&
      formData.date_palmares &&
      String(formData.titre_palmares || "").trim() &&
      !saving
    );
  }, [palmares?.id, formData, saving]);

  const updateField = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClose = () => {
    if (saving) return;

    if (
      !isDirty ||
      window.confirm("Des modifications ne sont pas enregistrées. Fermer ?")
    ) {
      onClose?.();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    await onSubmit?.(palmares.id, formData);
  };

  if (!isOpen || !palmares) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Modification
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Modifier le palmarès
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Ajustez le résultat, le classement, l’événement lié ou la
              visibilité sur l’accueil.
            </p>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 overflow-y-auto p-5"
        >
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
            <div className="flex items-center gap-3">
              <img
                src={getAvatarSrc(adherent?.photo_url)}
                alt={getMemberDisplayName(adherent)}
                className="h-14 w-14 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                onError={(event) => {
                  event.currentTarget.src = "/img/user_avatar.jpg";
                }}
              />

              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Adhérent
                </p>

                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  {getMemberDisplayName(adherent)}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Date
              </label>

              <input
                type="date"
                value={formData.date_palmares}
                onChange={(event) =>
                  updateField("date_palmares", event.target.value)
                }
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                required
                autoFocus
              />
            </div>

            <div className="flex items-end">
              <VisibilitySwitch
                checked={!!formData.is_active}
                onChange={() => updateField("is_active", !formData.is_active)}
                label="Afficher sur l’accueil"
              />
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 p-4 dark:border-slate-800">
            <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Événement lié
            </label>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
              <div className="relative">
                <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  value={eventQuery}
                  onChange={(event) => setEventQuery(event.target.value)}
                  placeholder="Rechercher un événement..."
                  className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                />
              </div>

              <button
                type="button"
                onClick={() => {
                  setEventQuery("");
                  updateField("event_palmares_id", "");
                }}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaEraser />
                Effacer
              </button>
            </div>

            <select
              value={formData.event_palmares_id}
              onChange={(event) =>
                updateField("event_palmares_id", event.target.value)
              }
              className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="">— Aucun événement —</option>
              {filteredEvents.map((event) => (
                <option key={event.id} value={event.id}>
                  {formatEventLabel(event)}
                </option>
              ))}
            </select>

            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
              Sélection actuelle :{" "}
              <span className="font-bold text-slate-700 dark:text-slate-200">
                {formData.event_palmares_id ? formatEventLabel(selectedEvent) : "—"}
              </span>
            </p>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Titre du palmarès
            </label>

            <input
              type="text"
              value={formData.titre_palmares}
              onChange={(event) =>
                updateField("titre_palmares", event.target.value)
              }
              placeholder="Ex : 1er au Tournoi Régional"
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Résultat
              </label>

              <input
                type="text"
                value={formData.resultats_palmares}
                onChange={(event) =>
                  updateField("resultats_palmares", event.target.value)
                }
                placeholder="Ex : 3 combats gagnés"
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Classement
              </label>

              <input
                type="text"
                value={formData.classement_palmares}
                onChange={(event) =>
                  updateField("classement_palmares", event.target.value)
                }
                placeholder="Ex : 2e, champion, qualifié..."
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Observations
            </label>

            <textarea
              value={formData.observations_palmares}
              onChange={(event) =>
                updateField("observations_palmares", event.target.value)
              }
              rows={6}
              placeholder="Notes, contexte, remarques..."
              className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            />
          </div>
        </form>

        <div className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            <button
              type="button"
              onClick={() => onDelete?.(palmares)}
              disabled={deletingId === palmares.id || saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-bold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
            >
              <FaTrash />
              Supprimer
            </button>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}